import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@corpuscomplete/web-ui/lib/utils";

const textVariants = cva("", {
  variants: {
    variant: {
      // Semantic variants include text-foreground for proper standalone color
      h1: "text-foreground scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl",
      h2: "text-foreground scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0",
      h3: "text-foreground scroll-m-20 text-2xl font-semibold tracking-tight",
      h4: "text-foreground scroll-m-20 text-xl font-medium tracking-tight",
      h5: "text-foreground scroll-m-20 text-lg font-medium tracking-tight",
      p: "text-foreground leading-7 [&:not(:first-child)]:mt-6",
      blockquote: "text-foreground mt-6 border-l-2 pl-6 italic",
      code: "text-foreground relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold",
      // Inline variants inherit color from parent (e.g., buttons)
      lg: "text-lg",
      md: "text-base",
      sm: "text-sm",
      xs: "text-xs",
    },
  },
  defaultVariants: {
    variant: "sm",
  },
});

type VariantPropType = VariantProps<typeof textVariants>;

const variantElementMap: Record<NonNullable<VariantPropType["variant"]>, React.ElementType> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  p: "p",
  blockquote: "blockquote",
  code: "code",
  lg: "span",
  md: "span",
  sm: "span",
  xs: "span",
};

export interface TextProps
  extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof textVariants> {
  as?: React.ElementType;
}

const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ className, variant, as, ...props }, ref) => {
    const Comp = as || variantElementMap[variant || "md"];
    return <Comp className={cn(textVariants({ variant, className }))} ref={ref} {...props} />;
  },
);
Text.displayName = "Text";

export { Text, textVariants };
